import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  apiErrorText,
  useCreateFieldTitulMutation,
  useGetFieldTitulsQuery,
  useListPtoRdDocumentsQuery,
  useSetFieldTitulDocumentsMutation,
  useUpdateFieldTitulMutation,
} from '@shared/api';
import type { FieldTitul } from '@entities/field';
import { projectDocumentSelection } from '@entities/field/tituls';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const Box = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin: 12px 0;
`;
const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 12px;
  margin: 10px 0;
`;
const Muted = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
const Error = styled.p.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
`;
const Label = styled.label`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 13px;
  margin: 8px 0;
`;
const Documents = styled.div`
  max-height: 340px;
  overflow: auto;
`;

export function ProjectTitulsPanel({
  projectId,
  canEdit,
}: {
  projectId: string;
  canEdit: boolean;
}) {
  const { currentData, isLoading, error } = useGetFieldTitulsQuery(projectId);
  const [create, { isLoading: creating }] = useCreateFieldTitulMutation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const tituls = currentData?.tituls.filter((t) => t.project_id === projectId) ?? [];
  async function submit(event: FormEvent) {
    event.preventDefault();
    setNote('');
    try {
      const result = await create({ projectId, code: code.trim(), name: name.trim() }).unwrap();
      setCode('');
      setName('');
      setEditing(result.titul.id);
    } catch (e) {
      setNote(apiErrorText(e, 'Не удалось создать титул.'));
    }
  }
  return (
    <Box>
      <h3>Титулы проекта</h3>
      <Muted>
        1. Создайте титулы. 2. Назначьте каждому его документы РД. 3. Ниже создайте в титуле
        площадки / фронты работ и назначьте прорабов. Каждый прораб сдаёт собственный рапорт и
        фотографии.
      </Muted>
      {isLoading && <Muted>Загрузка титулов…</Muted>}
      {error && <Error>{apiErrorText(error, 'Не удалось загрузить титулы.')}</Error>}
      {!error && !isLoading && tituls.length === 0 && (
        <Muted>
          Титулов пока нет. Существующие площадки и документы сохранены и автоматически не
          распределяются.
        </Muted>
      )}
      {tituls.map((titul) => (
        <Card key={titul.id}>
          <strong>
            {titul.code} — {titul.name}
          </strong>
          <Muted>Назначено документов РД: {titul.document_ids.length}</Muted>
          {canEdit && (
            <Button
              $size="sm"
              $variant="secondary"
              onClick={() => setEditing(editing === titul.id ? null : titul.id)}
            >
              {editing === titul.id ? 'Закрыть' : 'Название и РД титула'}
            </Button>
          )}
          {canEdit && editing === titul.id && (
            <TitulEditor key={`${projectId}-${titul.id}`} titul={titul} tituls={tituls} />
          )}
        </Card>
      ))}
      {canEdit && (
        <form onSubmit={submit}>
          <Row>
            <Label>
              Шифр титула
              <Input
                aria-label="Шифр титула"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="010"
                maxLength={80}
              />
            </Label>
            <Label>
              Название
              <Input
                aria-label="Название титула"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Участок сборки ГТО"
                maxLength={500}
              />
            </Label>
            <Button type="submit" disabled={creating || !code.trim() || !name.trim()}>
              Добавить титул
            </Button>
          </Row>
        </form>
      )}
      {note && <Error>{note}</Error>}
      <Muted>
        Нужна подсказка? В ОСНОВЕ есть <Link to="/assistant">помощник</Link>: попросите «Объясни по
        шагам, как создать титул, назначить прораба и принять рапорт».
      </Muted>
    </Box>
  );
}

function TitulEditor({ titul, tituls }: { titul: FieldTitul; tituls: FieldTitul[] }) {
  const docsQ = useListPtoRdDocumentsQuery({ projectId: titul.project_id });
  const [saveDocs, { isLoading: savingDocs }] = useSetFieldTitulDocumentsMutation();
  const [saveTitle, { isLoading: savingTitle }] = useUpdateFieldTitulMutation();
  const [selected, setSelected] = useState(titul.document_ids);
  const [code, setCode] = useState(titul.code);
  const [name, setName] = useState(titul.name);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const documents = useMemo(
    () =>
      (docsQ.currentData?.items ?? []).filter(
        (d) => d.project_id === titul.project_id && !d.deleted_at,
      ),
    [docsQ.currentData, titul.project_id],
  );
  const filtered = documents.filter((d) =>
    `${d.code} ${d.title} ${d.mark ?? ''}`.toLowerCase().includes(search.toLowerCase()),
  );
  const selectedSet = new Set(selected);
  const busy = savingDocs || savingTitle;
  return (
    <div>
      <Row>
        <Label>
          Шифр
          <Input value={code} onChange={(e) => setCode(e.target.value)} />
        </Label>
        <Label>
          Название
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Label>
        <Button
          $size="sm"
          disabled={busy || !name.trim() || !code.trim()}
          onClick={async () => {
            setNote(null);
            try {
              await saveTitle({ id: titul.id, code: code.trim(), name: name.trim() }).unwrap();
              setNote({ ok: true, text: 'Название сохранено.' });
            } catch (e) {
              setNote({ ok: false, text: apiErrorText(e, 'Не удалось изменить титул.') });
            }
          }}
        >
          Сохранить название
        </Button>
      </Row>
      <Muted>
        Выберите документы этого титула явно. Один документ относится к одному титулу. Общие
        документы оставьте нераспределёнными до решения инженера. Если конструктив уже создан,
        сервер запретит изменение состава РД титула для сохранения истории.
      </Muted>
      <Input
        aria-label="Поиск РД титула"
        placeholder="Шифр или название РД"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
      />
      {docsQ.isFetching && <Muted>Загрузка РД проекта…</Muted>}
      {docsQ.error && (
        <Error>
          {apiErrorText(docsQ.error, 'Не удалось загрузить РД. Изменения не отправлены.')}
        </Error>
      )}
      <Documents>
        {filtered.slice(page * 25, (page + 1) * 25).map((doc) => (
          <Label key={doc.id}>
            <input
              type="checkbox"
              checked={selectedSet.has(doc.id)}
              disabled={
                busy || tituls.some((t) => t.id !== titul.id && t.document_ids.includes(doc.id))
              }
              onChange={(e) =>
                setSelected((prev) =>
                  e.target.checked ? [...prev, doc.id] : prev.filter((id) => id !== doc.id),
                )
              }
            />
            {doc.code} — {doc.title}
            {tituls
              .filter((t) => t.id !== titul.id && t.document_ids.includes(doc.id))
              .map((t) => (
                <span key={t.id}> · уже в титуле {t.code}</span>
              ))}
          </Label>
        ))}
      </Documents>
      <Row>
        <Button
          $variant="ghost"
          $size="sm"
          disabled={page === 0}
          onClick={() => setPage((n) => n - 1)}
        >
          Назад
        </Button>
        <span>
          {filtered.length
            ? `${page * 25 + 1}–${Math.min((page + 1) * 25, filtered.length)} из ${filtered.length}`
            : 'Документов нет'}
        </span>
        <Button
          $variant="ghost"
          $size="sm"
          disabled={(page + 1) * 25 >= filtered.length}
          onClick={() => setPage((n) => n + 1)}
        >
          Далее
        </Button>
      </Row>
      <Button
        $size="sm"
        disabled={busy || docsQ.isFetching || !docsQ.currentData || !!docsQ.error}
        onClick={async () => {
          setNote(null);
          try {
            await saveDocs({
              id: titul.id,
              document_ids: projectDocumentSelection(documents, selected, titul.project_id),
            }).unwrap();
            setNote({ ok: true, text: 'РД титула сохранена.' });
          } catch (e) {
            setNote({ ok: false, text: apiErrorText(e, 'Не удалось назначить РД титула.') });
          }
        }}
      >
        Сохранить РД титула ({selected.length})
      </Button>
      {note && (note.ok ? <Muted role="status">{note.text}</Muted> : <Error>{note.text}</Error>)}
    </div>
  );
}
